import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { colors, typography } from '../theme/tokens';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Switch } from '../components/Switch';
import { supabase } from '../config/supabase';
import { useToast } from '../contexts/ToastContext';
import { ArrowLeft, Trash, X } from '@phosphor-icons/react';
import { Recipe } from '../types/recipe';
// @ts-ignore
import confetti from 'canvas-confetti';

const Container = styled.div`
  min-height: 100vh;
  padding: 1rem;
  overflow-y: auto;
`;

const Header = styled.div`
  background-color: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ContentContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const VideoContainer = styled.div`
  background: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding: 1rem;
  margin-bottom: 1rem;
`;

const Video = styled.video`
  width: 100%;
  max-height: 600px;
  border-radius: 4px;
`;

const DetailsContainer = styled.div`
  background: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${colors.neutral.charcoal};
`;

const SwitchGroup = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
`;

const DeleteButton = styled(Button)`
  background-color: ${colors.reds.bright};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;

  &:hover {
    background-color: ${colors.reds.dark};
  }
`;

const RecipesSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${colors.neutral.lightCharcoal};
`;

const RecipeChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${colors.brand.laurel};
  color: ${colors.neutral.white};
  padding: 12px;
  border-radius: 8px;
  font-family: ${typography.label.large.fontFamily};
  font-size: ${typography.label.large.fontSize};
  line-height: ${typography.label.large.lineHeight};
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const RecipeLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const RemoveChipButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${colors.neutral.white};
  cursor: pointer;
  padding: 0;
  margin: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const RecipeChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const RecipeList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${colors.neutral.white};
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
  display: none;

  &.visible {
    display: block;
  }
`;

const RecipeListItem = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.neutral.charcoal};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  background-color: ${colors.neutral.white};
  color: ${colors.neutral.black};
  &:focus {
    outline: none;
    border-color: ${colors.brand.laurel};
  }

  &:disabled {
    background-color: ${colors.neutral.offWhite};
    cursor: not-allowed;
  }
`;

export default function VideoDetails() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [video, setVideo] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isFree, setIsFree] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);
  const [showRecipeList, setShowRecipeList] = useState(false);
  const [recipeSearchTerm, setRecipeSearchTerm] = useState('');
  const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>([]);
  const recipesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchVideo = async () => {
      if (!user?.id || !videoId) return;
      setLoading(true);
      try {
        const { data: creatorDetails, error: creatorError } = await supabase
          .from('CreatorDetails')
          .select('id')
          .eq('AuthID', user.id)
          .single();

        if (creatorError) throw new Error('Failed to verify creator.');
        
        const creatorId = creatorDetails.id;

        const { data, error } = await supabase
          .from('Videos')
          .select('*')
          .eq('VideoId', videoId)
          .eq('CreatorID', creatorId)
          .single();

        if (error) throw error;
        if (!data) throw new Error('Video not found');

        setVideo(data);
        setTitle(data.Title || '');
        setDescription(data.Description || '');
        setIsFree(data.Free);
        setIsLive(data.Live);

        // Fetch associated recipes
        const { data: videoRecipes, error: videoRecipesError } = await supabase
          .from('VideoRecipes')
          .select('RecipeId')
          .eq('VideoId', videoId);

        if (videoRecipesError) throw videoRecipesError;

        if (videoRecipes && videoRecipes.length > 0) {
          const recipeIds = videoRecipes.map(vr => vr.RecipeId);
          const { data: recipesData, error: recipesError } = await supabase
            .from('Recipes')
            .select('*')
            .in('RecipeId', recipeIds);

          if (recipesError) throw recipesError;
          setSelectedRecipes(recipesData.map(r => r.RecipeId.toString()));
        }
      } catch (error) {
        showToast('Error loading video details', 'error');
        navigate('/videos');
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchVideo();
  }, [user, videoId, navigate, showToast]);

  useEffect(() => {
    const fetchRecipes = async () => {
      if (!user?.id) return;

      try {
        // Get the creator's ID from CreatorDetails using the auth user's ID
        const { data: creatorDetails, error: creatorError } = await supabase
          .from('CreatorDetails')
          .select('id')
          .eq('AuthID', user.id)
          .single();

        if (creatorError) {
          console.error('Error fetching creator details:', creatorError);
          return;
        }

        if (!creatorDetails) {
          console.error('No creator details found for user');
          return;
        }

        const creatorId = creatorDetails.id;

        const { data, error } = await supabase
          .from('Recipes')
          .select('*')
          .eq('UserID', creatorId)
          .order('DateCreated', { ascending: false });

        if (error) throw error;

        setRecipes(data || []);
        setFilteredRecipes(data || []);
      } catch (error) {
        console.error('Error fetching recipes:', error);
      }
    };

    fetchRecipes();
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (recipesRef.current && !recipesRef.current.contains(event.target as Node)) {
        setShowRecipeList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user?.id || !videoId) return;

    try {
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) throw new Error('Failed to verify creator for update.');

      const creatorId = creatorDetails.id;

      const { error } = await supabase
        .from('Videos')
        .update({
          Title: title,
          Description: description,
          Free: isFree,
          Live: isLive,
        })
        .eq('VideoId', videoId)
        .eq('CreatorID', creatorId);

      if (error) throw error;

      // Update video-recipe relationships
      const { error: deleteError } = await supabase
        .from('VideoRecipes')
        .delete()
        .eq('VideoId', videoId);

      if (deleteError) throw deleteError;

      if (selectedRecipes.length > 0) {
        const videoRecipePayload = selectedRecipes.map(recipeId => ({
          VideoId: videoId,
          RecipeId: parseInt(recipeId),
        }));

        const { error: insertError } = await supabase
          .from('VideoRecipes')
          .insert(videoRecipePayload);

        if (insertError) throw insertError;
      }

      showToast('Video updated successfully', 'success');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating video:', error);
      showToast('Error updating video', 'error');
    }
  };

  const handleRecipeSearch = (value: string) => {
    setRecipeSearchTerm(value);
    setShowRecipeList(true);
    if (!value.trim()) {
      setFilteredRecipes(recipes);
    } else {
      setFilteredRecipes(
        recipes.filter(recipe =>
          recipe.Name.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
  };

  const handleRecipeSelect = (recipe: Recipe) => {
    if (!selectedRecipes.includes(recipe.RecipeId.toString())) {
      setSelectedRecipes(prev => [...prev, recipe.RecipeId.toString()]);
    }
    setRecipeSearchTerm('');
    setShowRecipeList(false);
  };

  const handleRecipeRemove = (recipeId: string) => {
    setSelectedRecipes(prev => prev.filter(id => id !== recipeId));
  };

  const getSelectedRecipeNames = () => {
    return selectedRecipes.map(recipeId => {
      const recipe = recipes.find(r => r.RecipeId.toString() === recipeId);
      return recipe?.Name || '';
    });
  };

  const handleDelete = async () => {
    if (!user?.id || !videoId) return;
    
    if (!window.confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      return;
    }

    try {
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();
      
      if (creatorError) throw new Error('Failed to verify creator for delete.');

      const creatorId = creatorDetails.id;

      const { error } = await supabase
        .from('Videos')
        .delete()
        .eq('VideoId', videoId)
        .eq('CreatorID', creatorId);

      if (error) throw error;

      showToast('Video deleted successfully', 'success');
      navigate('/videos');
    } catch (error) {
      console.error('Error deleting video:', error);
      showToast('Error deleting video', 'error');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handlePublishToggle = async () => {
    if (!user?.id || !videoId) return;
    
    try {
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) throw new Error('Failed to verify creator for publish toggle.');

      const creatorId = creatorDetails.id;

      const { error } = await supabase
        .from('Videos')
        .update({ Live: !video.Live })
        .eq('VideoId', videoId)
        .eq('CreatorID', creatorId);

      if (error) throw error;

      // Fetch the updated video data
      const { data: updatedVideo, error: fetchError } = await supabase
        .from('Videos')
        .select('*')
        .eq('VideoId', videoId)
        .eq('CreatorID', creatorId)
        .single();

      if (fetchError) throw fetchError;

      setVideo(updatedVideo);
      setIsLive(updatedVideo.Live);
      showToast(
        updatedVideo.Live ? 'Video published successfully' : 'Video unpublished successfully',
        'success'
      );
      if (updatedVideo.Live) {
        confetti({
          particleCount: 200,
          spread: 180,
          origin: { y: 0.6 },
          colors: ['#177508', '#F6B83F', '#CFE531', '#E358C2', '#A0EA95', '#0E4805']
        });
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      showToast('Failed to update video status', 'error');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!video) {
    return <div>Video not found</div>;
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <BackButton onClick={() => navigate('/videos')}>
            <ArrowLeft size={20} />
            Back to Videos
          </BackButton>
          <ActionButtons>
            {isEditing ? (
              <>
                <Button variant="outline" color="danger" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>)}>
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleEdit}
                >
                  Edit
                </Button>
                <Button
                  variant={video.Live ? "outline" : "filled"}
                  color={video.Live ? "danger" : "primary"}
                  onClick={handlePublishToggle}
                >
                  {video.Live ? 'Unpublish' : 'Publish'}
                </Button>
              </>
            )}
          </ActionButtons>
        </HeaderContent>
      </Header>

      <ContentContainer>
        <VideoContainer>
          <Video
            controls
            src={`https://stream.mux.com/${video.MuxPlaybackId}.m3u8`}
            poster={`https://image.mux.com/${video.MuxPlaybackId}/thumbnail.png?time=2`}
          />
        </VideoContainer>

        <DetailsContainer>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={!isEditing}
              />
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!isEditing}
                placeholder="Enter video description"
              />
            </FormGroup>

            <SwitchGroup disabled={!isEditing}>
              <Label>Free</Label>
              <Switch
                checked={isFree}
                onChange={(checked) => setIsFree(checked)}
              />
            </SwitchGroup>

            <FormGroup>
              <Label>Created At</Label>
              <div>{new Date(video.created_at).toLocaleString()}</div>
            </FormGroup>

            <RecipesSection>
              <Label>Associated Recipes</Label>
              {isEditing ? (
                <div style={{ position: 'relative' }} ref={recipesRef}>
                  <Input
                    type="text"
                    value={recipeSearchTerm}
                    onChange={(e) => handleRecipeSearch(e.target.value)}
                    onFocus={() => setShowRecipeList(true)}
                    placeholder="Search recipes to add"
                  />
                  <RecipeList className={showRecipeList ? 'visible' : ''}>
                    {filteredRecipes
                      .filter(recipe => !selectedRecipes.includes(recipe.RecipeId.toString()))
                      .map((recipe) => (
                        <RecipeListItem
                          key={recipe.RecipeId}
                          onClick={() => handleRecipeSelect(recipe)}
                        >
                          {recipe.Name}
                        </RecipeListItem>
                      ))}
                  </RecipeList>
                </div>
              ) : null}

              {selectedRecipes.length > 0 ? (
                <RecipeChipsContainer>
                  {getSelectedRecipeNames().map((name, index) => (
                    <RecipeChip key={selectedRecipes[index]}>
                      {isEditing ? (
                        <>
                          {name}
                          <RemoveChipButton onClick={() => handleRecipeRemove(selectedRecipes[index])}>
                            <X size={20} />
                          </RemoveChipButton>
                        </>
                      ) : (
                        <RecipeLink
                          href={`/recipes/${selectedRecipes[index]}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/recipes/${selectedRecipes[index]}`);
                          }}
                        >
                          {name}
                        </RecipeLink>
                      )}
                    </RecipeChip>
                  ))}
                </RecipeChipsContainer>
              ) : (
                <EmptyState>
                  {isEditing ? (
                    <p>No recipes associated with this video. Search above to add some.</p>
                  ) : (
                    <p>No recipes associated with this video.</p>
                  )}
                </EmptyState>
              )}
            </RecipesSection>

            {isEditing && (
              <Button type="submit">Save Changes</Button>
            )}
          </Form>

          <DeleteButton onClick={handleDelete}>
            <Trash size={20} />
            Delete Video
          </DeleteButton>
        </DetailsContainer>
      </ContentContainer>
    </Container>
  );
} 